import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import Axios from 'axios'

import { getCurrentUser } from '../../../services/authService'
import { baseURL } from '../../../config'
import styles from './UserSearches.module.css'

import Toast from './Toast'

function UserNewSearch({ isModalOpen, closeModal, filters, isDarkMode, refreshInterval }) {
	const getDefaultName = () => {
		if (filters?.pickupLocations?.length) {
			return filters.pickupLocations[0].name
		}
		return 'Search Name'
	}

	const [loading, setLoading] = useState(false)
	const [searchName, setSearchName] = useState(getDefaultName())
	const [error, setError] = useState('')
	const [showToast, setShowToast] = useState(false)
	const secondaryButtonClasses = `inline-flex h-8 items-center justify-center rounded-md border px-3 text-xs font-medium transition-colors ${
		isDarkMode
			? 'border-[#444c56] bg-[#2d333b] text-[#adbac7] hover:bg-[#444c56]'
			: 'border-[#d0d7de] bg-[#e6e8ea] text-[#1c2128] hover:bg-[#d0d7de]'
	}`
	const submitButtonClasses = `inline-flex h-8 min-w-[76px] items-center justify-center gap-1.5 rounded-md px-3 text-xs font-semibold text-white transition-colors ${
		loading
			? 'cursor-not-allowed bg-emerald-600/70'
			: 'bg-emerald-600 hover:bg-emerald-700'
	}`

	const userToken = localStorage.getItem('jwtToken')
	const [searchParams] = useSearchParams()
	const tg_id = searchParams.get('tg_id')

	const handleOutsideClick = event => {
		if (event.target.tagName.toLowerCase() === 'dialog') {
			closeModal()
		}
	}

	useEffect(() => {
		if (tg_id && userToken) {
			async function callTgIdAssign() {
				try {
					await Axios.get(`${baseURL}/api/users/tg-id/assign/${tg_id}`, {
						headers: {
							authorization: userToken,
						},
					})

					setShowToast(true)

					setTimeout(() => {
						setShowToast(false)
					}, 3000)
				} catch (err) {
					console.log(err)
					setShowToast(false)
				}
			}

			callTgIdAssign()
		}
	}, [tg_id])

	useEffect(() => {
		setSearchName(getDefaultName())
	}, [filters.pickupLocations, isModalOpen])

	const createSearch = async () => {
		if (!searchName.trim()) {
			setError('Search name is required!')
			return
		}

		setError('')

		const user = getCurrentUser()

		const userToken = localStorage.getItem('jwtToken')
		const sources = filters?.sourceType?.map(s => s?.value)
		const defaultSources = ['TQL', 'DAT', 'COYOTE', 'JB HUNT', 'AMAZON']

		const pickUpInfo = {
			searchName: searchName,
			pickupLocations: filters.pickupLocations || [],
			dropoffLocations: filters.dropoffLocations || [],
			destinationStates: filters.destinationStates || [],
			destinationRegions: filters.destinationRegions || [],
			loadBoard: sources && sources.length ? sources : defaultSources,
			pickUpDeadhead: filters.pickupMaxDist,
			equipment:
				filters.equipType === 'Any' || !filters.equipType
					? null
					: filters.equipType,
			dropOffDeadhead: filters.dropoffMaxDist,
			userId: user.id,
			is_bot: false,
			isNotification: false,
			ignoreLoadOuts: filters.ignoreLoadOut,
			weight: filters.weight,
			stopCount: filters.stopCount,
			distance: filters.distance,
			filters: filters,
			refreshInterval: refreshInterval,
		}

		try {
			setLoading(true)

			await Axios.post(`${baseURL}/api/new-search`, pickUpInfo, {
				headers: {
					authorization: userToken,
				},
			})

			closeModal()
		} catch (err) {
			closeModal()
			setLoading(false)
			console.log(err, 'err')

			const errorMessage =
				err?.response?.data?.msg || err?.response?.data?.message

			setShowToast(true)
			setError(errorMessage || 'Something went wrong!')

			setTimeout(() => {
				setShowToast(false)
			}, 3000)
		} finally {
			setLoading(false)
			closeModal()
		}
	}

	if (showToast && !error) {
		return (
			<>
				<Toast message="Connected to Telegram successfully!" />
			</>
		)
	} else if (showToast && error) {
		return (
			<>
				<Toast message={error} error={true} />
			</>
		)
	}

	if (!filters.pickupLocations?.length && !filters?.bobtail) {
		return (
			<>
				<dialog
					onClick={handleOutsideClick}
					className={`modal ${isModalOpen ? 'modal-open' : ''}`}
				>
					<form
						method="dialog"
						className={`modal-box ${styles['searches-modal-box-custom']}`}
						style={{
							backgroundColor: isDarkMode ? '#1c2126' : '#f5f5f7',
							transition: 'background-color 0.3s ease',
						}}
					>
						<h2
							className={`font-bold text-lg ${
								isDarkMode ? 'text-amber-200' : 'text-amber-600'
							}`}
						>
							Oops! Origin field is required!
						</h2>
						<p
							className={`text-lg pt-4 py-2 ${
								isDarkMode ? 'text-gray-200' : 'text-gray-700'
							}`}
						>
							A search without specifying the origin city lacks a defined
							starting point. Please enter the origin city before proceeding.
						</p>

						<div className="modal-action">
							<button
								type="button"
								onClick={closeModal}
								className={secondaryButtonClasses}
							>
								Close
							</button>
						</div>
					</form>
				</dialog>
			</>
		)
	}

	// Removed Telegram connection requirement - users can now save searches without connecting to Telegram
	// Bot notifications will still work if user connects Telegram later

	return (
		<div>
			{isModalOpen && (
				<dialog
					onClick={handleOutsideClick}
					className={`modal ${isModalOpen ? 'modal-open' : ''}`}
				>
					<form
						method="dialog"
						className={`modal-box ${styles['searches-modal-box-custom']}`}
						style={{
							backgroundColor: isDarkMode ? '#1c2126' : '#f5f5f7',
							transition: 'background-color 0.3s ease',
						}}
					>
						<h3
							className={`font-bold text-base ${
								isDarkMode ? 'text-gray-200' : 'text-gray-800'
							}`}
						>
							Save New Search
						</h3>

						<div className="form-control w-full my-4">
							<input
								value={searchName}
								onChange={e => setSearchName(e.target.value)}
								type="text"
								placeholder="Name your search"
								className="input input-bordered min-h-0 h-9 w-full rounded-md px-3 text-sm"
								style={{
									backgroundColor: isDarkMode ? '#22272e' : '#ffffff',
									color: isDarkMode ? '#adbac7' : '#1c2128',
									borderColor: isDarkMode ? '#444c56' : '#d0d7de',
								}}
							/>
						</div>

						<div className="modal-action">
							<button
								type="button"
								onClick={closeModal}
								className={secondaryButtonClasses}
							>
								Close
							</button>

							<button
								type="button"
								onClick={createSearch}
								className={submitButtonClasses}
								disabled={loading}
							>
								{loading && (
									<span className="h-3 w-3 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
								)}
								Submit
							</button>
						</div>
					</form>
				</dialog>
			)}
		</div>
	)
}

export default UserNewSearch
